/**
 * Strip non-digits so inputs accept only integers (no decimals, e/E, +, -, or letters).
 * Use with `type="text"` + `inputMode="numeric"` instead of `type="number"`.
 */
export function digitsOnlyInput(value: string, maxDigits?: number): string {
  let s = value.replace(/\D/g, "");
  if (maxDigits != null && s.length > maxDigits) {
    s = s.slice(0, maxDigits);
  }
  return s;
}

/** Remove common Markdown copy artifacts before inserting text into a plain-text field. */
export function normalizePastedMarkdownText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/(?:&#x20;|&#32;|&nbsp;)/gi, " ")
    .replace(/\\+[ \t]*(?=\n|$)/g, "")
    .replace(/^([ \t]*)\\(-[ \t]+)/gm, "$1$2")
    .replace(/^([ \t]*-)[ \t]+/gm, "$1 ")
    .replace(/\\\*\\\*/g, "**")
    .replace(/\*\*(?=\S)([^\r\n]*?\S)\*\*/g, "$1")
    .replace(/__(?=\S)([^\r\n]*?\S)__/g, "$1");
}

/** Capitalize the first letter of every word without changing the remaining characters. */
export function capitalizeWords(value: string): string {
  return value.replace(/\b\w/g, (char) => char.toUpperCase());
}

/** @deprecated Use `capitalizeWords` for new fields. */
export function capitalizeFirst(value: string): string {
  return capitalizeWords(value);
}

export function toggleMenu(): void {
  const sidebar = document.querySelector("nav.custom-scrollbar");
  if (sidebar) {
    sidebar.classList.toggle("menu-open");
  }
  document.body.classList.toggle("menu-open");
}

// Jan 01
export function formatToMonthDay(dateStr?: string) {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
}

// September 01, 2025
export function formatToLongDate(dateValue?: string | Date | null, emptyLabel = "") {
  if (!dateValue) return emptyLabel;
  const dateOnly = typeof dateValue === "string" ? /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue) : null;
  const date = dateValue instanceof Date
    ? dateValue
    : dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return emptyLabel;

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
}

export function formatLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/** Backend `typeOfLeave` / leave keys (India, US, mixed) → display label. */
const LEAVE_TYPE_LABELS: Record<string, string> = {
  PTO_VACATION_REQUEST: "PTO Vacation Request",
  "PTO/Vacation Request": "PTO Vacation Request",
  SickLeave: "Sick Leave",
  PERSONAL_APPOINTMENT: "Personal Appointment",
  BEREAVEMENT: "Bereavement",
  MATERNITY: "Maternity Leave",
  PATERNITY: "Paternity Leave",
  PrivilegeLeave: "Privilege Leave",
  WorkFromHome: "Work From Home",
  CasualLeave: "Casual Leave",
  HalfDayLeave: "Half Day Leave",
  HalfYearly: "Half Yearly",
  Present: "Present",
  Absent: "Absent",
};

function humanizeLeaveKey(type: string): string {
  const t = type.trim();
  if (!t) return "";
  return t.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").trim();
}

export function formatLeaveType(type: string = ""): string {
  if (!type) return "";
  const mapped = LEAVE_TYPE_LABELS[type];
  if (mapped) return mapped;
  return humanizeLeaveKey(type) || type;
}

export const isToday = (date: Date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export function formatDecimalHoursToHoursAndMinutes(decimalHours: number): string {
  if (!Number.isFinite(decimalHours)) return "-";

  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

export function capitalizeNameWords(value: string): string {
  return value
    .toLowerCase()
    .replace(/(^|[\s'\u2019-])(\p{L})/gu, (_, separator: string, letter: string) => {
      return separator + letter.toUpperCase();
    });
}

export function validatePostalCode(value: string): string | undefined {
  if (!/^\d{5}$/.test(value.trim())) {
    return "Enter a valid 5-digit ZIP code.";
  }
}

export function formatEnumLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
